import type { LayerType } from '../types';
import type { AudioEngine } from '../audio/engine';
import type { GenMusPreset } from '../lib/storage';
import { savePreset, getPresets, deletePreset } from '../lib/storage';
import { updateStatus } from '../ui/status';

interface LayerState {
  id: string;
  type: LayerType;
  label: string;
  muted: boolean;
  volume: number;
  isAdditional?: boolean;
}

/**
 * Get current settings from UI
 */
export function getCurrentSettingsValues(layerStates: LayerState[]): GenMusPreset['params'] & { layers: Record<LayerType, { muted: boolean; volume: number }> } {
  // For presets, only save the base (non-additional) layers
  const baseLayers = layerStates.filter(l => !l.isAdditional);
  return {
    tempo: parseInt((document.getElementById('tempo-slider') as HTMLInputElement).value),
    density: parseInt((document.getElementById('density-slider') as HTMLInputElement).value),
    mood: parseInt((document.getElementById('mood-slider') as HTMLInputElement).value),
    reverb: parseInt((document.getElementById('reverb-slider') as HTMLInputElement).value),
    delay: parseInt((document.getElementById('delay-slider') as HTMLInputElement).value),
    compression: parseInt((document.getElementById('compression-slider') as HTMLInputElement).value),
    saturation: parseInt((document.getElementById('saturation-slider') as HTMLInputElement).value),
    smoothness: parseInt((document.getElementById('smoothness-slider') as HTMLInputElement).value),
    master: 70,
    layers: baseLayers.reduce((acc, layer) => {
      acc[layer.type] = { muted: layer.muted, volume: layer.volume };
      return acc;
    }, {} as Record<LayerType, { muted: boolean; volume: number }>),
  };
}

/**
 * Apply preset settings to UI and audio engine
 */
export function applySettings(
  settings: GenMusPreset['params'] & { layers?: Record<LayerType, { muted: boolean; volume: number }> },
  audioEngine: AudioEngine,
  layerStates: LayerState[],
  updateLayerTypeVolume: (layerType: LayerType) => void,
  updateMuteIconById: (layerId: string, muted: boolean) => void,
  syncVisualizerLayerStates: () => void
): void {
  (document.getElementById('tempo-slider') as HTMLInputElement).value = String(settings.tempo);
  (document.getElementById('density-slider') as HTMLInputElement).value = String(settings.density);
  (document.getElementById('mood-slider') as HTMLInputElement).value = String(settings.mood);
  document.getElementById('tempo-value')!.textContent = `${settings.tempo}`;
  document.getElementById('density-value')!.textContent = `${settings.density}%`;
  document.getElementById('mood-value')!.textContent = `${settings.mood}`;

  (document.getElementById('reverb-slider') as HTMLInputElement).value = String(settings.reverb);
  (document.getElementById('delay-slider') as HTMLInputElement).value = String(settings.delay);
  (document.getElementById('compression-slider') as HTMLInputElement).value = String(settings.compression ?? 30);
  (document.getElementById('saturation-slider') as HTMLInputElement).value = String(settings.saturation ?? 0);
  (document.getElementById('smoothness-slider') as HTMLInputElement).value = String(settings.smoothness);
  document.getElementById('reverb-value')!.textContent = `${settings.reverb}%`;
  document.getElementById('delay-value')!.textContent = `${settings.delay}%`;
  document.getElementById('compression-value')!.textContent = `${settings.compression ?? 30}%`;
  document.getElementById('saturation-value')!.textContent = `${settings.saturation ?? 0}%`;
  document.getElementById('smoothness-value')!.textContent = `${settings.smoothness}%`;

  audioEngine.updateParams({ tempo: settings.tempo, density: settings.density, mood: settings.mood, variation: 50 });
  audioEngine.setReverbAmount(settings.reverb / 100);
  audioEngine.setDelayAmount(settings.delay / 100);
  audioEngine.setCompressionAmount((settings.compression ?? 30) / 100);
  audioEngine.setSaturationAmount((settings.saturation ?? 0) / 100);
  audioEngine.setSmoothness(settings.smoothness);

  if (settings.layers) {
    Object.entries(settings.layers).forEach(([type, layerSettings]) => {
      const layerType = type as LayerType;
      const state = layerStates.find(l => l.type === layerType && !l.isAdditional);
      if (state) {
        state.muted = layerSettings.muted;
        state.volume = layerSettings.volume;

        const volumeSlider = document.querySelector(`.layer-volume[data-layer-id="${state.id}"]`) as HTMLInputElement;
        if (volumeSlider) volumeSlider.value = String(Math.round(layerSettings.volume * 100));

        updateLayerTypeVolume(layerType);
        updateMuteIconById(state.id, state.muted);
      }
    });
    syncVisualizerLayerStates();
  }
}

/**
 * Show save preset modal
 */
export function showSavePresetModal(
  layerStates: LayerState[],
  currentSampleId: string | null,
  audioEngine: AudioEngine
): void {
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.id = 'preset-modal';
  modal.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <span class="modal-title">Save Preset</span>
        <button class="modal-close" id="close-modal">×</button>
      </div>
      <input type="text" class="modal-input" id="preset-name-input" placeholder="Preset name..." />
      <div class="modal-actions">
        <button class="px-4 py-2 rounded-md text-xs font-medium cursor-pointer transition-all duration-150 bg-transparent text-[var(--color-foreground)] border border-[var(--color-border)] hover:bg-[var(--color-muted)]" id="cancel-preset">Cancel</button>
        <button class="px-4 py-2 rounded-md text-xs font-medium cursor-pointer transition-all duration-150 bg-[var(--color-foreground)] text-[var(--color-background)] border-0 hover:opacity-90" id="save-preset-confirm">Save</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  const input = document.getElementById('preset-name-input') as HTMLInputElement;
  input.focus();

  const closeModal = () => modal.remove();

  document.getElementById('close-modal')?.addEventListener('click', closeModal);
  document.getElementById('cancel-preset')?.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });

  document.getElementById('save-preset-confirm')?.addEventListener('click', () => {
    const name = input.value.trim() || 'Untitled Preset';
    const settings = getCurrentSettingsValues(layerStates);
    savePreset({
      name,
      sampleId: currentSampleId || undefined,
      params: {
        tempo: settings.tempo,
        density: settings.density,
        mood: settings.mood,
        reverb: settings.reverb,
        delay: settings.delay,
        compression: settings.compression,
        saturation: settings.saturation,
        smoothness: settings.smoothness,
        master: settings.master,
      },
      layers: settings.layers,
    });
    closeModal();
    updateStatus('Preset saved!', false);
    setTimeout(() => updateStatus(audioEngine.getIsPlaying() ? 'Playing' : 'Ready', audioEngine.getIsPlaying()), 1500);
  });
}

/**
 * Show load preset modal
 */
export function showLoadPresetModal(
  audioEngine: AudioEngine,
  layerStates: LayerState[],
  loadSampleFromLibrary: (sampleId: string) => Promise<void>,
  updateLayerTypeVolume: (layerType: LayerType) => void,
  updateMuteIconById: (layerId: string, muted: boolean) => void,
  syncVisualizerLayerStates: () => void
): void {
  const presets = getPresets();

  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.id = 'preset-modal';
  modal.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <span class="modal-title">Load Preset</span>
        <button class="modal-close" id="close-modal">×</button>
      </div>
      <div class="preset-list">
        ${presets.length === 0
      ? '<div class="empty-state">No presets saved</div>'
      : presets.map(p => `
            <div class="preset-item" data-preset-id="${p.id}">
              <div>
                <div class="preset-item-name">${p.name}</div>
                <div class="preset-item-date">${new Date(p.createdAt).toLocaleDateString()}</div>
              </div>
              <button class="w-6 h-6 rounded border-0 bg-transparent flex items-center justify-center cursor-pointer text-[var(--color-muted-foreground)] transition-all duration-150 hover:bg-[var(--color-card)] hover:text-[var(--color-foreground)] hover:!text-[#dc2626]" data-delete-preset="${p.id}" title="Delete">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                </svg>
              </button>
            </div>
          `).join('')
    }
      </div>
      <div class="modal-actions">
        <button class="px-4 py-2 rounded-md text-xs font-medium cursor-pointer transition-all duration-150 bg-transparent text-[var(--color-foreground)] border border-[var(--color-border)] hover:bg-[var(--color-muted)]" id="cancel-preset">Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  const closeModal = () => modal.remove();

  document.getElementById('close-modal')?.addEventListener('click', closeModal);
  document.getElementById('cancel-preset')?.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });

  modal.querySelectorAll('.preset-item').forEach(item => {
    item.addEventListener('click', async (e) => {
      if ((e.target as HTMLElement).closest('[data-delete-preset]')) return;

      const presetId = (item as HTMLElement).dataset.presetId;
      if (presetId) {
        const preset = getPresets().find(p => p.id === presetId);
        if (preset) {
          applySettings(
            { ...preset.params, layers: preset.layers },
            audioEngine,
            layerStates,
            updateLayerTypeVolume,
            updateMuteIconById,
            syncVisualizerLayerStates
          );

          if (preset.sampleId) {
            await loadSampleFromLibrary(preset.sampleId);
          }

          closeModal();
          updateStatus('Preset loaded!', false);
          setTimeout(() => updateStatus(audioEngine.getIsPlaying() ? 'Playing' : 'Ready', audioEngine.getIsPlaying()), 1500);
        }
      }
    });
  });

  modal.querySelectorAll('[data-delete-preset]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const presetId = (btn as HTMLElement).dataset.deletePreset;
      if (presetId && confirm('Delete this preset?')) {
        deletePreset(presetId);
        closeModal();
        showLoadPresetModal(
          audioEngine,
          layerStates,
          loadSampleFromLibrary,
          updateLayerTypeVolume,
          updateMuteIconById,
          syncVisualizerLayerStates
        );
      }
    });
  });
}
